import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { BasicLayout } from '@monorepo/common';

import { useAppSelector } from '../../store/hooks';
import { StepperPanel } from './stepper/StepperPanel';
import { jobDataApi, useLazyGetJobDataQuery } from '../../services/serviceJobData';
import { MetaDescription } from '../../components/MetaDescription';
import { NoAccessMessage } from '../../components/NoAccessMessage';
import { Hosts } from '../../constants/hostName';
import { setPageTitle } from '../../utils/setPageTitle';
import {
  isForbidden,
  isNotFound,
  LISTING_NOT_FOUND_MESSAGE,
  LISTING_LOAD_ERROR_MESSAGE,
} from '../../utils/jobAccessError';

// interface IEditJobProps {}
export const EditJob = () => {
  const [getJobData] = useLazyGetJobDataQuery();
  const [titleBrowserPage, setTitleBrowserPage] = useState<string>('Jobs');

  const { siteId } = useAppSelector(({ pathnameSettings }) => ({
    siteId: pathnameSettings.siteNumber
  }));

  useEffect(() => {
    setPageTitle({
      firstTitle: 'Edit Job Listing',
      siteNumber: siteId || Hosts.BEVNET,
      setTitle: setTitleBrowserPage
    });
  }, [siteId]);

  const { guid } = useParams<{ guid: string }>();
  // The lazy hook's `data` can be the previous query's result while another guid
  // loads. Select the cache entry for this guid so Edit never mounts with another
  // listing's data (and cannot save that data under the new guid).
  const selectCurrentJob = useMemo(() => jobDataApi.endpoints.getJobData.select({
    siteId: siteId.toString(),
    guid: guid || ''
  }), [guid, siteId]);
  const {
    data, isError, isSuccess, error,
  } = useAppSelector(selectCurrentJob);
  const isLoading = Boolean(guid && !data && !isError && !isSuccess);

  useEffect(() => {
    if (guid) {
      getJobData({
        siteId: siteId.toString(),
        guid
      });
    }
  }, [getJobData, guid, siteId]);

  // A 403 (self-service access removed), a 404 / empty response (removed or deleted
  // listing), or any other load failure would otherwise leave the page blank, since
  // the body only renders when `data` is present. Surface a clear message in-place.
  const buildLoadError = () => {
    if (isForbidden(error)) {
      return <NoAccessMessage />;
    }
    if (isNotFound(error) || (isSuccess && !data)) {
      return (
        <NoAccessMessage
          title="Listing not found"
          message={LISTING_NOT_FOUND_MESSAGE}
        />
      );
    }
    if (isError) {
      return (
        <NoAccessMessage
          title="Unable to load listing"
          message={LISTING_LOAD_ERROR_MESSAGE}
        />
      );
    }
    return undefined;
  };

  return (
    <BasicLayout
      isLoading={isLoading}
      hiddenContentWhenIsLoading={isLoading}
      noAccessComponent={buildLoadError()}
      helmet={MetaDescription({ title: titleBrowserPage })}
    >
      {guid && data && (
      <div data-cy="editJob">
        <StepperPanel
          key={guid}
          data={data}
          guid={guid}
        />
      </div>
      )}
    </BasicLayout>
  );
};
